import { useCallback, useEffect, useRef, useState } from "react";
import { useFormMetadata } from "../contexts/FormMetadataContext";

/**
 * Custom hook to manage subsection expand/collapse state with localStorage persistence
 * @author Assistant
 */
export const useSubsectionExpand = (subsections = []) => {
  const { formTitle, formId } = useFormMetadata();
  const [expandedSubsections, setExpandedSubsections] = useState(new Set());
  const hasInitializedRef = useRef(false);

  /**
   * Generate a unique storage key for this form
   * Uses formId if available, otherwise falls back to formTitle
   */
  const getStorageKey = useCallback(() => {
    const identifier = formId || formTitle?.replace(/\s+/g, "_") || "default";
    return `formCanvas_expandedSections_${identifier}`;
  }, [formId, formTitle]);

  /**
   * Save expanded state to localStorage
   */
  const saveExpandedState = useCallback(
    (expandedSet) => {
      try {
        const expandedIds = Array.from(expandedSet);
        localStorage.setItem(getStorageKey(), JSON.stringify(expandedIds));
      } catch (error) {
        console.warn("Failed to save expanded state to localStorage:", error);
      }
    },
    [getStorageKey]
  );

  /**
   * Load expanded state from localStorage
   */
  const loadExpandedState = useCallback(() => {
    try {
      const saved = localStorage.getItem(getStorageKey());
      if (saved) {
        const expandedIds = JSON.parse(saved);
        // Filter out IDs that no longer exist in current subsections
        const currentSubsectionIds = new Set(subsections.map((sub) => sub.id));
        const validIds = expandedIds.filter((id) => currentSubsectionIds.has(id));
        return new Set(validIds);
      }
    } catch (error) {
      console.warn("Failed to load expanded state from localStorage:", error);
    }

    // Fallback: return first subsection expanded if available
    return subsections.length > 0 ? new Set([subsections[0].id]) : new Set();
  }, [getStorageKey, subsections]);

  /**
   * Clean up localStorage when subsections are removed
   */
  const cleanupExpandedState = useCallback(() => {
    try {
      const saved = localStorage.getItem(getStorageKey());
      if (saved && subsections.length > 0) {
        const expandedIds = JSON.parse(saved);
        const currentSubsectionIds = subsections.map((sub) => sub.id);
        const validIds = expandedIds.filter((id) => currentSubsectionIds.includes(id));

        if (validIds.length !== expandedIds.length) {
          localStorage.setItem(getStorageKey(), JSON.stringify(validIds));
          setExpandedSubsections(new Set(validIds));
        }
      }
    } catch (error) {
      console.warn("Failed to cleanup localStorage:", error);
    }
  }, [getStorageKey, subsections]);

  /**
   * Initialize expanded state from localStorage or set default
   */
  useEffect(() => {
    if (!hasInitializedRef.current && subsections.length > 0) {
      const loadedState = loadExpandedState();
      setExpandedSubsections(loadedState);
      hasInitializedRef.current = true;
    }
  }, [subsections, loadExpandedState]);

  /**
   * Save to localStorage whenever expanded state changes
   */
  useEffect(() => {
    if (hasInitializedRef.current) {
      saveExpandedState(expandedSubsections);
    }
  }, [expandedSubsections, saveExpandedState]);

  /**
   * Clean up invalid IDs when subsections change
   */
  useEffect(() => {
    if (hasInitializedRef.current) {
      cleanupExpandedState();
    }
  }, [subsections.map((s) => s.id).join(","), cleanupExpandedState]);

  /**
   * Toggle expand/collapse state for a specific subsection
   */
  const toggleExpanded = useCallback((subsectionId) => {
    setExpandedSubsections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(subsectionId)) {
        newSet.delete(subsectionId);
      } else {
        newSet.add(subsectionId);
      }
      return newSet;
    });
  }, []);

  /**
   * Expand all subsections
   */
  const expandAll = useCallback(() => {
    if (subsections.length > 0) {
      const allSubsectionIds = subsections.map((sub) => sub.id);
      setExpandedSubsections(new Set(allSubsectionIds));
    }
  }, [subsections]);

  /**
   * Collapse all subsections
   */
  const collapseAll = useCallback(() => {
    setExpandedSubsections(new Set());
  }, []);

  /**
   * Expand a specific subsection (useful for newly added subsections)
   */
  const expandSubsection = useCallback((subsectionId) => {
    setExpandedSubsections((prev) => new Set([...prev, subsectionId]));
  }, []);

  /**
   * Check if a specific subsection is expanded
   */
  const isExpanded = useCallback(
    (subsectionId) => {
      return expandedSubsections.has(subsectionId);
    },
    [expandedSubsections]
  );

  /**
   * Check if all subsections are expanded
   */
  const areAllExpanded = subsections.length > 0 ? subsections.every((sub) => expandedSubsections.has(sub.id)) : false;

  /**
   * Get count of expanded subsections
   */
  const expandedCount = expandedSubsections.size;

  return {
    // State
    expandedSubsections,
    expandedCount,
    areAllExpanded,

    // Actions
    toggleExpanded,
    expandAll,
    collapseAll,
    expandSubsection,
    isExpanded,

    // Utils
    getStorageKey,
  };
};
